import * as React from 'react';
import { useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { MenuItem, Select } from '@material-ui/core';
import history from '../history';
import { setUserInfo, userInfoSelector } from '../redux/ducks/userAuth';
import DefaultUserLogo from '../resources/icons/person.svg';

import { UserDropMenu } from '../molecules';
import { TextWithDropIcon, Tooltip } from '../atoms';
import getModuleName from '../utils/getModuleName';
import { getCurrentUser } from '../api/userSettings';
import { RootState } from '../redux/root';
import { ROUTES } from '../constants/routes';
import InvoiceTool from '../Components/InvoiceTool/InvoiceTool';

const UserMenu = () => {
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const userInfo = useSelector(userInfoSelector);

  React.useEffect(() => {
    setAvatarBroken(false);
  }, [userInfo?.avatar]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  }, []);
  const [selectedFranchise, setSelectedFranchise] = React.useState(-1);

  React.useEffect(() => {
    const userInfoStr = localStorage.getItem('userInfo');
    const userInfo = JSON.parse(userInfoStr);
    const _selectedFranchise = userInfo.selectedFranchise || userInfo.defaultFranchiseId;
    setSelectedFranchise(_selectedFranchise);
    getCurrentUser(_selectedFranchise).then(currentUser => {
      const user = currentUser.data;
      dispatch(setUserInfo({ ...user, selectedFranchise: _selectedFranchise }));
    });
  }, []);

  const handleChangeBranch = e => {
    const franchiseId = e.target.value;
    const _userInfo = { ...userInfo, selectedFranchise: franchiseId };
    // Must stay before the dispatch: auth.ts setUserData re-reads selectedFranchise
    // from the existing localStorage entry and ignores the payload's value.
    localStorage.setItem('userInfo', JSON.stringify(_userInfo));
    dispatch(setUserInfo(_userInfo));
    if (/\/permit\/[0-9]+\/[0-9]+$/.test(window.location.pathname)) {
      history.push(ROUTES.DASHBOARD);
    }
    // The server keeps the active franchise in the Django session and only updates it
    // when a request carries ?franchise_id=. Reloading first would race that write, and
    // the next page would render the previous branch until a second manual refresh.
    Promise.race([
      getCurrentUser(franchiseId).catch(() => null),
      // Never strand the switcher if the call hangs.
      new Promise(resolve => setTimeout(resolve, 3000)),
    ]).then(() => window.location.reload());
  };

  const userName = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ');

  return (
    <UserMenuWrap>
      {(getModuleName() === 'EMPLOYEE MODULE MANAGEMENT') && (
        <BranchPill>
          <BranchLabel>Branch</BranchLabel>
          <BranchSelect
            value={selectedFranchise}
            onChange={handleChangeBranch}
            disableUnderline
          >
            {userInfo.franchise?.map((branch, index) => (
              <MenuItem key={index} value={branch?.id}>{branch?.name}</MenuItem>
            ))}
          </BranchSelect>
        </BranchPill>
      )}
      <AvatarButton
        onClick={handleOpen}
        ref={menuRef}
      >
        {userInfo?.avatar && !avatarBroken ? (
          <AvatarImg
            src={userInfo.avatar}
            alt=""
            onError={() => setAvatarBroken(true)}
          />
        ) : (
          <AvatarCircle>
            {(userInfo?.firstName?.[0] || '').toUpperCase()}{(userInfo?.lastName?.[0] || '').toUpperCase()}
          </AvatarCircle>
        )}
        {userName && <AvatarName>{userName}</AvatarName>}
      </AvatarButton>

      <UserDropMenu
        handleClose={handleClose}
        open={isOpen}
        anchorRef={menuRef}
      />
    </UserMenuWrap>
  );
};

const UserMenuWrap = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const BranchPill = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-1) var(--space-1) var(--space-3);
  background: var(--neutral-50);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-full);
  min-width: 0;

  @media (max-width: 540px) {
    padding: var(--space-1) var(--space-2);
    max-width: 160px;
  }
`;

const BranchLabel = styled.span`
  font-size: var(--fs-sm);
  color: var(--neutral-500);
  font-weight: var(--fw-medium);
  white-space: nowrap;

  @media (max-width: 540px) {
    display: none;
  }
`;

const BranchSelect = styled(Select)`
  font-size: var(--fs-sm) !important;
  font-weight: var(--fw-semibold) !important;
  color: var(--neutral-800) !important;
  font-family: inherit !important;

  .MuiSelect-select {
    padding: 2px 24px 2px 0 !important;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .MuiSvgIcon-root {
    color: var(--neutral-500);
    width: 18px;
    height: 18px;
  }
`;

const AvatarButton = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--neutral-100);
  }
`;

const AvatarImg = styled.img`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  object-fit: cover;
  flex-shrink: 0;
  background: var(--neutral-100);
`;

const AvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  background: var(--primary-600);
  color: var(--neutral-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  flex-shrink: 0;
  text-transform: uppercase;
`;

const AvatarName = styled.span`
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--neutral-700);
  white-space: nowrap;

  @media (max-width: 960px) {
    display: none;
  }
`;

export default UserMenu;
